import { useEffect, useState } from "react";
import { cn } from "../../lib/cn.js";
import { Button, Card, CardDescription, CardTitle, Input, Select, StatusBadge, Textarea } from "../ui/index.js";
import { SurelcReadinessList, SurelcRequestList, type SurelcNote } from "../surelc/SurelcStatusLists.js";
import { API_BASE, type AgentDetailData } from "./types.js";

export interface StaffChoice {
  id: string;
  name: string;
}

export function useSurelcStaffChoices(token: string | null) {
  const [staff, setStaff] = useState<StaffChoice[]>([]);
  const [viewerId, setViewerId] = useState<string | null>(null);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void fetch(`${API_BASE}/api/surelc-tasks/staff`, { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => response.json())
      .then((json) => {
        if (cancelled || !json?.success) return;
        setStaff(Array.isArray(json.data?.staff) ? json.data.staff : []);
        setViewerId(json.data?.viewerId ?? null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token]);
  return { staff, viewerId };
}

export async function patchSurelcTask(
  token: string,
  agentId: string,
  noteId: string,
  body: { assigneeId?: string | null; dueAt?: string | null; completed?: boolean }
) {
  const response = await fetch(`${API_BASE}/api/agents/${agentId}/surelc-notes/${noteId}`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await response.json().catch(() => null);
  if (!response.ok || !json?.success) throw new Error(json?.error || "Unable to update task");
}

function formatDay(value: string | null): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleDateString();
}

function NoteRow({
  note,
  staff,
  busy,
  onPatch,
}: {
  note: SurelcNote;
  staff: StaffChoice[];
  busy: boolean;
  onPatch: (body: { assigneeId?: string | null; dueAt?: string | null; completed?: boolean }) => void;
}) {
  const isTask = Boolean(note.assigneeId || note.dueAt);
  const overdue = Boolean(!note.completedAt && note.dueAt && new Date(note.dueAt).getTime() < Date.now());
  const assignee = staff.find((user) => user.id === note.assigneeId);
  return (
    <li className={cn("rounded-xl bg-surface-overlay px-3 py-2 text-sm", note.completedAt && "opacity-70")}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className={cn("text-content-primary", note.completedAt && "line-through")}>{note.body}</p>
        {isTask ? (
          <Button
            size="sm"
            variant={note.completedAt ? "ghost" : "secondary"}
            disabled={busy}
            onClick={() => onPatch({ completed: !note.completedAt })}
          >
            {note.completedAt ? "Reopen" : "Mark done"}
          </Button>
        ) : null}
      </div>
      <p className="mt-1 flex flex-wrap gap-x-3 text-xs text-content-muted">
        <span>{new Date(note.createdAt).toLocaleString()}</span>
        {isTask ? <span>Assigned to {assignee?.name ?? (note.assigneeId ? "someone else" : "nobody")}</span> : null}
        {note.dueAt ? (
          <span className={cn(overdue && "font-semibold text-status-rejected-text")}>
            Due {formatDay(note.dueAt)}
            {overdue ? " · overdue" : ""}
          </span>
        ) : null}
        {note.completedAt ? <span>Done {formatDay(note.completedAt)}</span> : null}
      </p>
    </li>
  );
}

export function SurelcOnboardingPanel({
  surelc,
  agentId,
  token,
  onChanged,
}: {
  surelc: NonNullable<AgentDetailData["surelc"]>;
  agentId: string;
  token: string | null;
  onChanged: () => Promise<void> | void;
}) {
  const { staff, viewerId } = useSurelcStaffChoices(token);
  const [note, setNote] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  async function run(key: string, action: () => Promise<void>) {
    setBusy(key);
    setError(null);
    try {
      await action();
      await onChanged();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Something went wrong");
    } finally {
      setBusy(null);
    }
  }

  async function addNote() {
    if (!token) return;
    await run("note", async () => {
      const response = await fetch(`${API_BASE}/api/agents/${agentId}/surelc-notes`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ body: note, assigneeId: assigneeId || null, dueAt: dueAt || null }),
      });
      const json = await response.json().catch(() => null);
      if (!response.ok || !json?.success) throw new Error(json?.error || "Unable to save note");
      setNote("");
      setAssigneeId("");
      setDueAt("");
    });
  }

  async function refreshFromSurelc() {
    if (!token) return;
    setBusy("sync");
    setError(null);
    setSyncMessage(null);
    try {
      const response = await fetch(`${API_BASE}/api/agents/${agentId}/surelc-sync`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await response.json().catch(() => null);
      if (!response.ok || !json?.success) throw new Error(json?.error || "Unable to refresh from SureLC");
      setSyncMessage("Refresh queued. Reload in a minute to see the result.");
    } catch (syncError) {
      setError(syncError instanceof Error ? syncError.message : "Unable to refresh from SureLC");
    } finally {
      setBusy(null);
    }
  }

  const openTasks = surelc.notes.filter((entry) => (entry.assigneeId || entry.dueAt) && !entry.completedAt).length;

  return (
    <Card padding="lg" className="space-y-5 border-2">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>SureLC onboarding</CardTitle>
            <StatusBadge
              status={surelc.staffAttention ? "NIGO" : surelc.producerId ? "ACTIVE" : "PENDING"}
              size="sm"
            />
          </div>
          <CardDescription className="mt-1">{surelc.nextAction}</CardDescription>
        </div>
        <div className="space-y-1 text-right text-xs text-content-secondary">
          <p>Producer: {surelc.producerId ?? "Not linked"}</p>
          <p>Mode: {surelc.mode === "simulated" ? "Simulated (test data, not in SureLC)" : surelc.mode ?? "Not started"}</p>
          <p>Last SureLC sync: {surelc.syncedAt ? new Date(surelc.syncedAt).toLocaleString() : "Never"}</p>
          <p>
            Proceed:{" "}
            {surelc.proceededAt
              ? `clicked ${formatDay(surelc.proceededAt)}`
              : surelc.canProceed
                ? "available"
                : "not yet"}
            {surelc.proceedRequired ? " · gates dialer setup" : ""}
          </p>
          {surelc.producerId ? (
            <Button size="sm" variant="secondary" disabled={busy === "sync"} onClick={() => void refreshFromSurelc()}>
              {busy === "sync" ? "Queuing…" : "Refresh from SureLC"}
            </Button>
          ) : null}
        </div>
      </div>

      {surelc.syncError ? (
        <p className="rounded-xl border border-status-rejected-border bg-status-rejected-bg px-3 py-2 text-xs text-status-rejected-text">
          Last SureLC sync had an error: {surelc.syncError}
        </p>
      ) : null}
      {syncMessage ? <p className="text-xs text-content-secondary">{syncMessage}</p> : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {surelc.steps.map((step) => (
          <div key={step.id} className="rounded-xl border border-border-default bg-surface-overlay p-3">
            <p className="text-sm font-bold text-content-primary">{step.label}</p>
            <p className="mt-1 text-xs leading-5 text-content-secondary">{step.detail}</p>
            <p className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-content-muted">
              {step.status} · {step.owner}
            </p>
          </div>
        ))}
      </div>

      {surelc.producerId ? (
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="space-y-2">
            <h3 className="text-sm font-bold text-content-primary">SureLC profile items</h3>
            <SurelcReadinessList items={surelc.readiness} />
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-bold text-content-primary">Carrier requests in SureLC</h3>
            <SurelcRequestList requests={surelc.requests} />
            {surelc.contracts.length > 0 ? (
              <>
                <h3 className="pt-2 text-sm font-bold text-content-primary">SureLC contracts</h3>
                <ul className="space-y-1 text-sm">
                  {surelc.contracts.map((contract) => (
                    <li key={contract.id} className="flex flex-wrap justify-between gap-2 rounded-lg bg-surface-overlay px-3 py-1.5">
                      <span className="font-semibold text-content-primary">{contract.carrier}</span>
                      <span className="text-content-secondary">
                        {[contract.status, contract.agentNo ? `Writing # ${contract.agentNo}` : null]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      {surelc.routing.length > 0 ? (
        <div>
          <h3 className="text-sm font-bold text-content-primary">Carrier routing</h3>
          <ul className="mt-2 grid gap-2 sm:grid-cols-2">
            {surelc.routing.map((route) => (
              <li
                key={route.carrier}
                className="rounded-xl border border-border-default bg-surface-overlay px-3 py-2 text-sm"
              >
                <span className="font-semibold text-content-primary">{route.carrier}</span>
                <span className="ml-2 text-content-secondary">
                  {route.configured
                    ? `${route.method}${route.destination ? ` · ${route.destination}` : ""}`
                    : "Route missing"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div>
        <h3 className="text-sm font-bold text-content-primary">
          Internal notes and tasks
          {openTasks > 0 ? <span className="ml-2 text-xs font-semibold text-accent">{openTasks} open</span> : null}
        </h3>
        <p className="mt-1 text-xs text-content-muted">
          Agents never see these. Give a note an assignee or a due date to put it on the onboarding task list.
        </p>
        <div className="mt-2 space-y-2">
          <Textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={1000}
            rows={3}
            aria-label="Add an internal SureLC note"
            placeholder="What is the agent waiting on?"
          />
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Select
              value={assigneeId}
              onChange={(event) => setAssigneeId(event.target.value)}
              aria-label="Assign to"
              className="sm:w-56"
            >
              <option value="">No assignee</option>
              {viewerId ? <option value={viewerId}>Me</option> : null}
              {staff
                .filter((user) => user.id !== viewerId)
                .map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
            </Select>
            <Input
              type="date"
              value={dueAt}
              onChange={(event) => setDueAt(event.target.value)}
              aria-label="Due date"
              className="sm:w-44"
            />
            <Button className="shrink-0" disabled={busy === "note" || !note.trim()} onClick={() => void addNote()}>
              {busy === "note" ? "Saving…" : assigneeId || dueAt ? "Add task" : "Add note"}
            </Button>
          </div>
        </div>
        {error ? (
          <p className="mt-2 text-sm text-status-rejected-text" role="alert">
            {error}
          </p>
        ) : null}
        {surelc.notes.length > 0 ? (
          <ul className="mt-3 space-y-2">
            {surelc.notes.map((entry) => (
              <NoteRow
                key={entry.id}
                note={entry}
                staff={staff}
                busy={busy === entry.id}
                onPatch={(body) => {
                  if (!token) return;
                  void run(entry.id, () => patchSurelcTask(token, agentId, entry.id, body));
                }}
              />
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-xs text-content-muted">No internal notes yet.</p>
        )}
      </div>
    </Card>
  );
}
